import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { ShieldAlert } from "lucide-react";
import { useI18n } from "../lib/i18n";

const fmt = (d) => d.toISOString().slice(0, 10);

export default function BreakAudit() {
  const { t } = useI18n();
  const now = new Date();
  const [start, setStart] = useState(fmt(new Date(now.getTime() - 30 * 86400000)));
  const [end, setEnd] = useState(fmt(now));
  const [event, setEvent] = useState("");
  const [rows, setRows] = useState([]);

  const load = async () => {
    const p = new URLSearchParams({ start_date: start, end_date: end });
    if (event) p.set("event", event);
    const { data } = await api.get(`/break-audit?${p.toString()}`);
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const accidental = rows.filter((r) => r.event === "accidental").length;

  return (
    <div className="space-y-6" data-testid="break-audit-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{t("ba.title")}</h1>
        <p className="text-sm font-medium tracking-wide uppercase text-stone-500">{t("ba.sub")}</p>
      </div>

      <div className="bg-white border border-stone-200 p-4 shadow-sm flex flex-wrap gap-3 items-end">
        <div><label className="text-[10px] font-bold uppercase block mb-1">{t("ts.start")}</label><input data-testid="ba-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-10 px-2 border border-stone-200 text-sm" /></div>
        <div><label className="text-[10px] font-bold uppercase block mb-1">{t("ts.end")}</label><input data-testid="ba-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-10 px-2 border border-stone-200 text-sm" /></div>
        <select data-testid="ba-event" value={event} onChange={(e) => setEvent(e.target.value)} className="h-10 px-2 border border-stone-200 text-sm"><option value="">{t("ba.all_events")}</option><option value="completed">{t("ba.completed")}</option><option value="accidental">{t("ba.accidental")}</option></select>
        <button data-testid="ba-run" onClick={load} className="h-10 px-5 bg-[#1F3D34] text-white font-bold uppercase text-sm">{t("ba.run")}</button>
        {accidental > 0 && <span className="text-xs font-bold text-amber-700 flex items-center gap-1"><ShieldAlert size={14} /> {accidental} {t("ba.accidental_count")}</span>}
      </div>

      <div className="bg-white border border-stone-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b-2 border-stone-300 text-left text-xs">
            <tr>
              <th className="p-3">{t("ba.datetime")}</th>
              <th className="p-3">{t("ba.employee")}</th>
              <th className="p-3">{t("ba.type")}</th>
              <th className="p-3">{t("ba.event")}</th>
              <th className="p-3">{t("bt.started")}</th>
              <th className="p-3">{t("ts.end")}</th>
              <th className="p-3">{t("ba.duration")}</th>
              <th className="p-3">{t("ts.job")}</th>
              <th className="p-3">{t("ba.device")}</th>
              <th className="p-3">GPS</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={10} className="p-6 text-center text-stone-500 font-bold uppercase text-xs">{t("ba.none")}</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-b border-stone-100" data-testid={`ba-row-${r.id}`}>
                <td className="p-3 font-mono text-xs">{new Date(r.created_at).toLocaleString()}</td>
                <td className="p-3 font-bold">{r.employee_name || "—"}</td>
                <td className="p-3 uppercase text-xs">{r.break_type === "meal" ? t("bt.meal") : t("bt.rest")}</td>
                <td className="p-3">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 border ${r.event === "accidental" ? "bg-amber-100 border-amber-500 text-amber-800" : "bg-[#7C9673]/20 border-[#7C9673] text-[#1F3D34]"}`}>
                    {r.event === "accidental" ? t("ba.accidental") : t("ba.completed")}
                  </span>
                </td>
                <td className="p-3 font-mono text-xs">{r.start ? new Date(r.start).toLocaleTimeString() : "—"}</td>
                <td className="p-3 font-mono text-xs">{r.end ? new Date(r.end).toLocaleTimeString() : "—"}</td>
                <td className="p-3 font-mono text-xs">{r.duration_minutes != null ? `${r.duration_minutes}m` : "—"}</td>
                <td className="p-3 font-mono text-xs">{r.job_code || "—"}</td>
                <td className="p-3 font-mono text-[10px] truncate max-w-[120px]" title={`${r.device_id || ""} ${r.device_info || ""}`}>{r.device_id ? r.device_id.slice(0, 8) : "—"}</td>
                <td className="p-3 font-mono text-[10px]">{r.latitude != null ? `${r.latitude.toFixed(3)}, ${r.longitude.toFixed(3)}` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
